import { useEffect, useState, useRef } from "react";
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Dialog, DialogTitle,
  DialogContent, TextField, DialogActions
} from "@mui/material";
import { useDispatch } from "react-redux";

import httpClient from "../../_util/api";
import FileUpload from "../../components/FileUpload";
import { UploadImagetoFb } from "../../Hooks/uploadImage";
import ButtonMain from "../../components/Button";
import CustomizedSnackbars from "../../shared/Snackbar/SnackBar";
import { showLoader, hideLoader } from "../../Store/mainSlice";

const ReimbursementAmount = () => {
  const dispatch = useDispatch();
  const snackbarRef = useRef();
  const challanPhotoRef = useRef();

  const [reimbursements, setReimbursements] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [amount, setAmount] = useState("");
  const [remarks, setRemarks] = useState("");
  const [voucher, setVoucher] = useState({ fileName: "", url: "" });

  useEffect(() => {
    fetchReimbursements();
  }, []);

  const fetchReimbursements = async () => {
    try {
      const res = await httpClient.get("/vendor-payment/my-reimbursement");
      if (res.status === 200 && Array.isArray(res.data?.data)) {
        setReimbursements(res.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch reimbursements:", err);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    dispatch(showLoader());
    try {
      const uploaded = await UploadImagetoFb(event);
      setVoucher({
        fileName: file.name,
        url: uploaded.url,
      });
    } catch {
      alert("File upload failed.");
    } finally {
      dispatch(hideLoader());
    }
  };

  const handleAmountChange = (e) => {
    const clean = e.target.value.replace(/,/g, "").replace(/[^\d]/g, "");
    setAmount(clean);
  };

  const formatWithCommas = (value) => {
    const num = value.replace(/,/g, "");
    if (!num) return "";
    return parseFloat(num).toLocaleString("en-US");
  };

  const handleSubmit = async () => {
    dispatch(showLoader());
    try {
      const payload = {
        type: "reimbursement",
        amount: Number(amount),
        remarks,
        voucher,
        reimbursementStatus: "unPaid",
      };

      const res = await httpClient.post("/vendor-payment/", payload);
      if (res.status === 200) {
        snackbarRef.current.showMessage("success", res.data.message || "Submitted");
        fetchReimbursements();
        resetForm();
        setDialogOpen(false);
      }
    } catch (error) {
      snackbarRef.current.showMessage(
        "error",
        error?.response?.data?.message || "Submission failed"
      );
    } finally {
      dispatch(hideLoader());
    }
  };

  const resetForm = () => {
    setAmount("");
    setRemarks("");
    setVoucher({ fileName: "", url: "" });
  };

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "16px" }}>
        <ButtonMain onClick={() => setDialogOpen(true)}>Reimbursement Request</ButtonMain>
      </div>

      <Paper sx={{ width: "100%", overflow: "hidden", padding: 2 }}>
        <CustomizedSnackbars ref={snackbarRef} />

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>Apply For Reimbursement</DialogTitle>
          <DialogContent>
            <TextField
              label="Amount"
              type="text"
              fullWidth
              margin="normal"
              onChange={handleAmountChange}
              value={formatWithCommas(amount)}
            />
            <FileUpload
              ref={challanPhotoRef}
              label="Upload Paid Challan"
              file={voucher}
              onChange={handleFileUpload}
              onClick={() => challanPhotoRef.current?.click()}
              onRemove={() => setVoucher({ fileName: "", url: "" })}
            />
            <TextField
              label="Remarks"
              multiline
              rows={4}
              fullWidth
              margin="normal"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <ButtonMain variant="white" onClick={() => { setDialogOpen(false); resetForm(); }}>
              Cancel
            </ButtonMain>
            <ButtonMain onClick={handleSubmit}>Submit</ButtonMain>
          </DialogActions>
        </Dialog>

        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: "#4caf50" }}>
              <TableRow>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>#</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Amount</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Paid Challan</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Remarks</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reimbursements.map((entry, index) => (
                <TableRow key={index}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{formatWithCommas(String(entry.amount))}</TableCell>
                  <TableCell>
                    {entry.voucher?.url ? (
                      <a
                        href={entry.voucher.url}
                        target="_blank"
                        style={{ color: "#4caf50", textDecoration: "none", fontWeight: "bold" }}
                        onMouseOver={e => (e.target.style.textDecoration = "underline")}
                        onMouseOut={e => (e.target.style.textDecoration = "none")}
                      >
                        {entry.voucher.fileName || "View Voucher"}
                      </a>
                    ) : (
                      "—"
                    )}
                  </TableCell>

                  <TableCell>{entry.remarks || "—"}</TableCell>
                  <TableCell
                    sx={{
                      color: entry.reimbursementStatus?.toLowerCase() === "unpaid" ? "red" : "green",
                      fontWeight: "bold",
                      textTransform: "uppercase",
                    }}
                  >
                    {entry.reimbursementStatus || "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
};

export default ReimbursementAmount;
